export interface BusinessLineSlotSummaryInput {
  plan?: string | null;
  used: number;
}

export interface BusinessLineSlotSummary {
  includedSlots: number;
  usedSlots: number;
  openSlots: number;
  extraSlots: number;
  title: string;
  helper: string;
}

export function includedBusinessLineSlots(plan?: string | null): number {
  const normalized = (plan ?? '').trim().toLowerCase();
  if (normalized === 'solo') return 1;
  if (normalized === 'team') return 3;
  return 0;
}

export function businessLineSlotSummary(input: BusinessLineSlotSummaryInput): BusinessLineSlotSummary {
  const includedSlots = includedBusinessLineSlots(input.plan);
  const usedSlots = Math.max(0, input.used);
  const openSlots = Math.max(0, includedSlots - usedSlots);
  const extraSlots = Math.max(0, usedSlots - includedSlots);
  const planName = planLabel(input.plan);

  if (includedSlots === 0) {
    return {
      includedSlots,
      usedSlots,
      openSlots,
      extraSlots,
      title: usedSlots > 0 ? `${usedSlots} active business line${plural(usedSlots)}` : 'No included business line slots yet',
      helper: 'Choose a plan before provisioning Oracord-managed numbers. Existing numbers can still be mapped for testing.',
    };
  }

  if (openSlots > 0) {
    return {
      includedSlots,
      usedSlots,
      openSlots,
      extraSlots,
      title: `${openSlots} open business line slot${plural(openSlots)}`,
      helper: usedSlots === 0 && includedSlots === 1
        ? 'Your plan includes 1 business line. Pick a person and choose a number when you are ready.'
        : `Your plan includes ${includedSlots} business lines. Numbers are provisioned only when you set up a slot.`,
    };
  }

  return {
    includedSlots,
    usedSlots,
    openSlots,
    extraSlots,
    title: `${Math.min(usedSlots, includedSlots)} of ${includedSlots} included slots used`,
    helper: extraSlots > 0
      ? `${extraSlots} active line${plural(extraSlots)} ${extraSlots === 1 ? 'is' : 'are'} beyond the included ${planName} slots. Extra business-slot billing can be handled from Billing.`
      : 'All included business line slots are active. Add another slot from Billing if another team member needs a line.',
  };
}

function planLabel(plan?: string | null): string {
  const normalized = (plan ?? '').trim().toLowerCase();
  if (normalized === 'solo') return 'Solo';
  if (normalized === 'team') return 'Team';
  return 'plan';
}

function plural(count: number): string {
  return count === 1 ? '' : 's';
}
